import { useCallback, useEffect, useRef, useState } from 'react';

import { Skeleton } from '@displate/styleguide';
import clsx from 'clsx';

import {
  mapPillsPickerOptions,
  sumValuesFrom,
} from '@/common/components/PillsPicker/utils/pillsPicker.utils';

import styles from './pillsPicker.module.scss';

export interface Option<ValueType> {
  label?: string;
  value?: ValueType;
  testId?: string;
  disabled?: boolean;
}

interface Props<ValueType> {
  options: Option<ValueType>[];
  activeOption: ValueType;
  onChange: (option: ValueType) => void;
  className?: string;
  variant?: 'transparent';
  capitalize?: boolean;
}

const PillsPicker = <ValueType extends string | number>({
  options: optionsArray,
  activeOption,
  onChange,
  className,
  variant,
  capitalize = true,
}: Props<ValueType>) => {
  const [gliderWidth, setGliderWidth] = useState(0);
  const options = mapPillsPickerOptions(optionsArray);
  const activeOptionIndex = options.findIndex((option) => option.value === activeOption);
  const labelRefs = useRef<HTMLLabelElement[]>([]);

  const setGlider = useCallback(() => {
    setGliderWidth(labelRefs.current[activeOptionIndex]?.offsetWidth || 0);
  }, [activeOptionIndex]);

  useEffect(() => {
    if (labelRefs.current.length) setGlider();

    window.addEventListener('resize', setGlider);
    return () => {
      window.removeEventListener('resize', setGlider);
    };
  }, [activeOptionIndex, labelRefs.current.length, setGlider]);

  const getTranslateValue = () => {
    const widths = labelRefs.current.map((ref) => ref.offsetWidth);
    return sumValuesFrom(widths, activeOptionIndex);
  };

  return (
    <div
      className={clsx(styles.container, className, {
        [styles.variantTransparent]: variant === 'transparent',
      })}
      data-testid="pills-picker"
    >
      {gliderWidth > 0 && (
        <span
          className={styles.glider}
          style={{ transform: `translateX(${getTranslateValue()}px)`, width: gliderWidth + 'px' }}
        />
      )}

      {options.map((option, i) => {
        const checked = activeOption === option.value;

        return (
          <label
            className={clsx(styles.option, {
              [styles.activeOption]: checked,
              [styles.loading]: !option.label,
              [styles.disabled]: option.disabled,
              [styles.activeOptionSkeleton]: checked && !gliderWidth,
              [styles.capitalizeOff]: capitalize === false,
            })}
            key={option.value}
            onClick={setGlider}
            // option.label sometimes is loaded asynchronously, ie. Size values@PDP, so we need to wait for them to calculate the width
            ref={(labelRef) => labelRef && option.label && (labelRefs.current[i] = labelRef)}
          >
            <input
              checked={checked}
              className={styles.radio}
              data-testid={option.testId || option.value}
              disabled={option.disabled}
              name={`${option.value}`}
              onChange={(): void => onChange(option.value as ValueType)}
              type="radio"
              value={option.value}
            />
            {option.label ? (
              <span>{option.label}</span>
            ) : (
              <Skeleton
                className={styles.skeleton}
                data-testid="pills-picker-label-skeleton"
                width={55}
              />
            )}
          </label>
        );
      })}
    </div>
  );
};

export default PillsPicker;
